import {
  DocumentTemplateType,
  QueryFactory,
  TemplateDto,
} from '@lmt/shared-types';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CustomCommandExecutedEventArgs,
  DXR,
  RichEdit,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import { gdtHeaderTitle } from './AddGDTHeaderRichEditButton';
import { useEventCallback } from '@mui/material';

const showUpdateHeaderAndFooterButtonId = 'showUpdateHeaderAndFooterButtonId';
export function isTemplateHeaderOrFooter(template: TemplateDto | null) {
  return (
    template?.type === DocumentTemplateType.DocumentHeader ||
    template?.type === DocumentTemplateType.DocumentFooter ||
    (template?.type === DocumentTemplateType.GDTOutgoingMessage &&
      template.title === gdtHeaderTitle)
  );
}

async function getBase64(richEdit: RichEdit): Promise<string> {
  return new Promise<string>((resolve, _reject) =>
    richEdit.exportToBase64(function (res) {
      resolve(res);
    }),
  );
}

export async function updateHeaderAndFooterAsync(
  templateId: number,
  patientId: number,
  richEdit: RichEdit,
) {
  richEdit.loadingPanel.show();
  richEdit.beginUpdate();
  try {
    const base64 = await getBase64(richEdit);
    const updatedBase64 =
      await QueryFactory.DocumentClient.updateDocumentHeaderAndFooter(
        templateId,
        patientId,
        { content: base64 },
      );
    richEdit.openDocument(
      updatedBase64,
      richEdit.documentName,
      undefined,
      (importSuccess) => {
        if (importSuccess) {
          setTimeout(() => {
            richEdit.hasUnsavedChanges = true;
          }, 1);
        }
      },
    );
  } catch (e) {
    console.error('updateHeaderAndFooterAsync', e);
  } finally {
    richEdit.loadingPanel.hide();
    richEdit.endUpdate();
  }
}

interface Props {
  templateId: number;
  patientId: number;
  richEdit: React.MutableRefObject<RichEdit>;
}
export const UpdateHeaderAndFooter: React.FC<Props> = (props) => {
  const { richEdit } = props;
  const i18n = useTranslation();

  useEffect(function AddButtonsToRibbon() {
    const richEditCurrent = richEdit.current;

    richEditCurrent.updateRibbon((ribbon) => {
      if (!ribbon) return;
      const filePanel = ribbon.getTab(DXR.RibbonTabType.Insert)!;
      filePanel.insertItem(
        new DXR.RibbonButtonItem(
          showUpdateHeaderAndFooterButtonId,
          i18n.t('documents.refresh_header_and_footer_button'),
          {
            icon: 'dxre-icon-ShowAllFieldResults',
            showText: true,
          },
        ),
      );
    });

    return () => {
      if (richEdit.current?.isDisposed === false) {
        richEditCurrent.updateRibbon((ribbon) => {
          if (!ribbon) return;
          const filePanel = ribbon.getTab(DXR.RibbonTabType.Insert)!;
          filePanel.removeItem(showUpdateHeaderAndFooterButtonId);
        });
      }
    };
  }, []);

  const updateHeaderAndFooterHandler = useEventCallback(
    async (
      sender: RichEdit | undefined,
      args: CustomCommandExecutedEventArgs | undefined,
    ) => {
      if (!sender || !args) {
        return;
      }

      if (args.commandName === showUpdateHeaderAndFooterButtonId) {
        await updateHeaderAndFooterAsync(
          props.templateId,
          props.patientId,
          richEdit.current,
        );
      }
    },
  );

  useEffect(
    function AddHandlerForButtons() {
      richEdit.current.events.customCommandExecuted.addHandler(
        updateHeaderAndFooterHandler,
      );
      return () => {
        richEdit.current.events.customCommandExecuted.removeHandler(
          updateHeaderAndFooterHandler,
        );
      };
    },
    [updateHeaderAndFooterHandler],
  );

  return null;
};
